/**
 * TanStack Query mutation for creating a project.
 *
 * Replaces `useProjectsStore.createProject`. Optimistically prepends
 * the new project to the cached list on success (mirrors the prior
 * Zustand behaviour where the list was updated in-place after a
 * successful create).
 *
 * Follows the visibility-domain mutation template — see
 * `useAddBrandPrompt` in `webapp/hooks/visibility/useBrandPrompts.ts`.
 */

import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";
import { ProjectCreateResponse } from "@ripenn/api-contracts";
import { safeFetch } from "@/lib/api/safe-fetch";
import { buildApiUrl } from "@/lib/api-config";
import { projectKeys } from "@/lib/queryKeys";
import type { CreateProjectRequest, Project } from "@/lib/projects/types";

export function useCreateProject(): UseMutationResult<
  ProjectCreateResponse,
  Error,
  CreateProjectRequest
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateProjectRequest) =>
      safeFetch(ProjectCreateResponse, buildApiUrl("/api/projects"), {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: ({ project }) => {
      queryClient.setQueryData<Project[]>(projectKeys.list(), (prev) =>
        prev ? [project, ...prev] : [project],
      );
    },
  });
}
